import { styled, css } from '../../../../modules';
import { keyframes } from 'styled-components';

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
    overflow: hidden;
`;

export const LogoSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 48px;
    max-width: 200px;
    object-fit: contain;
`;

export const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    overflow-y: auto;
    padding: 24px 24px 0;
`;

export const HeroSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
`;

export const Title = styled.h1<{ $color: string }>`
    font-size: 22px;
    font-weight: 600;
    color: ${({ $color }) => $color};
    margin: 0;
    text-align: center;
`;

export const Subtitle = styled.p`
    font-size: 13px;
    color: #555;
    margin: 0;
    text-align: center;
    line-height: 1.5;
`;

/* ── Step indicator ────────────────────────────── */

export const StepRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 4px;
`;

export const StepIndicatorContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding: 12px;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    margin-bottom: 24px;
`;

/* Wraps multiple inactive circles so they sit flush together */
export const StepGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

/* Wraps the active circle + its label */
export const StepActiveItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const StepCircle = styled.div<{ $active: boolean; $color: string; $clickable?: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
    background-color: ${({ $active, $color }) => ($active ? $color : 'transparent')};
    color: ${({ $active, $color }) => ($active ? '#ffffff' : '#999')};
    border: 2px solid ${({ $active, $color }) => ($active ? $color : '#ccc')};
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

    ${({ $clickable, $color }) =>
        $clickable &&
        `
        &:hover {
            border-color: ${$color};
            color: ${$color};
        }
    `}
`;

export const StepLabel = styled.span<{ $color: string }>`
    font-size: 14px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    white-space: nowrap;
`;

export const StepSeparator = styled.div`
    width: 100%;
    height: 1px;
    background-color: #e5e5e5;
    margin-bottom: 20px;
`;

/* ── Form ───────────────────────────────────────── */

export const SectionTitle = styled.h2<{ $color: string }>`
    font-size: 20px;
    font-weight: 400;
    color: ${({ $color }) => $color};
    margin: 0 0 16px;
`;

export const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SelectGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const SelectLabel = styled.label`
    font-size: 12px;
    font-weight: 700;
    color: #666;
`;

export const StyledSelect = styled.select`
    width: 100%;
    background-color: #ffffff;
    border: 1px solid #ccc;
    height: 40px;
    border-radius: 4px;
    padding: 0 32px 0 10px;
    font-size: 14px;
    font-family: inherit;
    color: #333;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    cursor: pointer;

    option {
        font-size: 14px;
        background-color: #f8f8f8;
        color: #333;
    }
`;

/* ── Payment toggle ─────────────────────────────── */

export const PaymentToggle = styled.div`
    display: flex;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #ccc;
    margin-bottom: 4px;
`;

export const ToggleButton = styled.button<{ $active: boolean; $color: string }>`
    flex: 1;
    padding: 10px;
    border: none;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    background-color: ${({ $active, $color }) => ($active ? $color : '#ffffff')};
    color: ${({ $active, $color }) => ($active ? '#ffffff' : $color)};
    transition: background-color 0.15s, color 0.15s;
    margin-top: 0;
    transform: none;

    &:active {
        transform: none;
    }
`;

/* ── Warning callout ────────────────────────────── */

export const Callout = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background-color: #fff8e6;
    border: 1px solid #f5c842;
    border-radius: 6px;
    padding: 10px 12px;
`;

export const CalloutIcon = styled.span`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: #c49102;
`;

export const CalloutText = styled.p`
    font-size: 12px;
    color: #555;
    margin: 0;
    line-height: 1.5;
`;

export const ScrollEnd = styled.div`
    height: 24px;
    flex-shrink: 0;
`;

/* ── Footer ─────────────────────────────────────── */

export const FooterSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 12px;
    padding: 16px 24px 20px;
    border-top: 1px solid #e5e5e5;
    flex-shrink: 0;
    background-color: #ffffff;
`;

/* ── Payment confirmation bottom sheet ──────────── */

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to   { opacity: 0; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const slideDown = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(100%); }
`;

export const ModalOverlay = styled.div<{ $closing?: boolean }>`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${fadeOut} 0.3s ease forwards;`
            : css`animation: ${fadeIn} 0.3s ease forwards;`}
`;

export const ModalSheet = styled.div<{ $closing?: boolean }>`
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 12px 24px 36px;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${slideDown} 0.3s ease forwards;`
            : css`animation: ${slideUp} 0.3s ease forwards;`}
`;

export const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: #ddd;
    align-self: center;
    margin-bottom: 4px;
`;

export const SheetTitle = styled.h3<{ $color: string }>`
    font-size: 18px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 0;
`;

export const ConfirmRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const ConfirmLabel = styled.span`
    font-size: 12px;
    color: #888;
`;

export const ConfirmValue = styled.div`
    width: 100%;
    background-color: #f0f0f0;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 14px;
    color: #333;
    text-align: center;
`;

export const SheetButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
`;

export const SheetConfirmButton = styled.button<{ $color: string }>`
    width: 100%;
    padding: 15px 20px;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $color }) => $color};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active { opacity: 0.85; }
`;

export const SheetEditButton = styled.button`
    width: 100%;
    padding: 13px 20px;
    border: 1.5px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: #666;
    background-color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active { background-color: #f5f5f5; }
`;

export const SubmitButton = styled.button<{ $color: string; $disabled: boolean }>`
    width: 100%;
    padding: 14px 20px;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $color, $disabled }) => ($disabled ? '#bbb' : $color)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;
